import React from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary,
  Box, Chip, List, ListItemButton, ListItemText, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { ProviderSummary } from '../../types/billing';

interface Props {
  providers: ProviderSummary[];
  onProviderClick: (provider: ProviderSummary) => void;
}

interface BillerBucket {
  billerId: string;
  billerName: string;
  providers: ProviderSummary[];
}

function groupByBiller(providers: ProviderSummary[]): BillerBucket[] {
  const byId = new Map<string, BillerBucket>();
  for (const p of providers) {
    for (let i = 0; i < p.billerIds.length; i += 1) {
      const id = p.billerIds[i];
      const name = p.billerNames[i] ?? '(unknown)';
      let bucket = byId.get(id);
      if (!bucket) {
        bucket = { billerId: id, billerName: name, providers: [] };
        byId.set(id, bucket);
      }
      bucket.providers.push(p);
    }
  }
  return Array.from(byId.values()).sort((a, b) =>
    a.billerName.localeCompare(b.billerName),
  );
}

const ProvidersByBiller: React.FC<Props> = ({ providers, onProviderClick }) => {
  const buckets = groupByBiller(providers);

  if (buckets.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No billers assigned to any of the listed providers.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {buckets.map((b) => (
        <Accordion key={b.billerId} disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{b.billerName}</Typography>
              <Chip label={`${b.providers.length} providers`} size="small" />
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <List dense disablePadding>
              {b.providers.map((p) => (
                <ListItemButton key={`${b.billerId}:${p.id}`} onClick={() => onProviderClick(p)}>
                  <ListItemText
                    primary={`${p.firstName} ${p.lastName}`}
                    secondary={`${p.practiceName}${p.specialty ? ` · ${p.specialty}` : ''}`}
                  />
                </ListItemButton>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default ProvidersByBiller;
